import moment from 'moment'
import { TaskModel } from '@/app/models/tasks/taskModel'
import { useBusinessTripFailuresStore } from '@/modules/drivers/stores/businessTripFailuresStore'

type BatchBusinessTripResult = {
  DriverId?: number
  driverId?: number
  Success?: boolean
  success?: boolean
  Skipped?: boolean
  skipped?: boolean
  RequiresCoDriverDecision?: boolean
  requiresCoDriverDecision?: boolean
  Code?: string
  code?: string
  Message?: string
  message?: string
}

const normalizeScopeDate = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return ''
  const parsed = moment(value as moment.MomentInput)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

/**
 * Parse a completed batch business-trip generation task and store the drivers
 * that failed (keyed by driverId) so the business-trips list can flag them with
 * a per-driver marker + reason. Scoped to the company + period the generation
 * ran for; always overwrites the previous set for that scope, so a clean re-run
 * clears stale markers.
 *
 * Called from every poller that handles `TaskType.BusinessTrip` completion
 * (`TaskStatus` and `TaskProgressStatus`) — whichever is mounted on the page.
 */
export const recordBusinessTripFailures = (
  companyId: string,
  task: TaskModel,
): void => {
  // The completion poller's `companyId` prop comes from
  // `companySelection.selectedCompany`, which is empty when the user reaches the
  // page directly by URL. The task's own `companyId` (echoed by the status
  // endpoint) is authoritative and matches the route GUID the list is scoped to.
  const scopeCompanyId =
    (task as { companyId?: string }).companyId || companyId
  const from = normalizeScopeDate(task.data?.defaultStartDate)
  const to = normalizeScopeDate(task.data?.defaultEndDate)
  if (!scopeCompanyId || !from || !to) return

  const failures: Record<string, { code?: string; message?: string }> = {}
  try {
    const results = JSON.parse(task.logs ?? '') as BatchBusinessTripResult[]
    if (Array.isArray(results)) {
      results.forEach((result) => {
        const success = result.Success ?? result.success
        const skipped = result.Skipped ?? result.skipped
        const requiresCoDriver =
          result.RequiresCoDriverDecision ??
          result.requiresCoDriverDecision ??
          (result.Code ?? result.code) === 'COPY_TO_CODRIVER'
        if (success || skipped || requiresCoDriver) return

        const driverId = result.DriverId ?? result.driverId
        if (driverId === undefined || driverId === null) return

        failures[String(driverId)] = {
          code: result.Code ?? result.code,
          message: result.Message ?? result.message,
        }
      })
    }
  } catch {
    // Malformed logs — record no per-driver failures (aggregate toast still shows).
  }

  useBusinessTripFailuresStore
    .getState()
    .setFailures({ companyId: scopeCompanyId, from, to }, failures)
}
